import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Alert, Badge, Button, Form, Spinner, Table,
} from 'react-bootstrap';
import Error from '../../../Components/Error';
import ValidationErrorList from '../../../Components/ValidationErrorList';
import AiInstructionsService, {
  EXECUTING_AUTHORITY,
  draftConfigFrom,
  grantedTools,
  isAuthorityAllowed,
  maxLengthFor,
  modeDescriptor,
  modeOf,
  sameAuthority,
} from '../../../Services/AiInstructionsService';
import PublishInstructionsModal from '../Components/PublishInstructionsModal';
import AgentToolMap from '../Components/AgentToolMap';
import ComposedPrompt from '../Components/ComposedPrompt';
import GuidanceKnowledgeBase from '../Components/GuidanceKnowledgeBase';
import AgentDryRun from '../Components/AgentDryRun';
import { formatDateTime } from '../../../Utils/timezone';

/**
 * The Agent Builder area of AI Admin: what an agent may call, the rules that
 * drive it, and a rehearsal — all without a deploy.
 *
 * This is an AREA, registered in ../areas.js and rendered inside the AI Admin
 * shell (../index.jsx) at /ai-admin/agent-instructions. The SHELL owns the
 * eyebrow, the page's <h1> and the area selector; this file renders no
 * heading of its own, because there is one <h1> per page and it is the area's
 * label from ../areas.js.
 *
 * ── THE ONE THING THIS SCREEN IS FOR ──
 * Keeping "I am editing" and "the agent is using" visibly apart. The text in
 * the editor steers how a production agent handles real customers, so an
 * edit left half-typed over lunch must not read as live, and a save must not
 * be mistakable for a publish. That is why they are two panels, two buttons,
 * two different words, and why PUBLISH IS NOT REACHABLE FROM UNSAVED TEXT —
 * the publish call takes a stored version, never the contents of the box.
 *
 * ── WHY THE TOOLS AND THE DRY RUN LIVE HERE AND NOT ON SCREENS OF THEIR OWN ──
 * Because they are not separate things on the server. The prose, the scope's
 * `mode` and the `tool_authority` map are THREE COLUMNS OF ONE ROW in
 * `agent_instructions`: they are validated against each other on the way in
 * (a tool the prose names must be a tool the scope holds, or the save is
 * refused), there is exactly ONE draft per scope, and one publish freezes all
 * three under one version — which is what makes "what was this agent allowed
 * to do when it ran" the same lookup as "what was it told".
 *
 * Two screens over one draft row would therefore mean two saves racing for
 * it, and a refusal about prose landing on whoever was editing tools. So
 * there is one draft, one Save and one Publish, and the panels below are
 * ordered as the job is done: what is live, what I am writing, what it may
 * call, what happened when I tried it, what publishing will do.
 *
 * ── SCOPES ──
 * One scope is one agent's guidance, and the list comes from the server
 * (`/ai/instructions/scopes`). There is no free-text scope field: scopes are
 * code-owned, and a screen that let somebody type one would write guidance
 * nothing reads. Nothing here is special-cased per scope either — the second
 * and fifth agent land as server-side list entries and this file does not
 * change.
 *
 * The open scope is component state rather than a URL segment or `?scope=`:
 * the shell's route is /ai-admin/<area> with no slot for a second level, and
 * a query string would be a second source of truth for which scope is open
 * while adding nothing — the screen is reached from one nav point.
 *
 * ── PERMISSION ──
 * No in-screen permission check, deliberately. Every control on this screen
 * is a managing control, and the route and the nav entry already gate the
 * whole area on `ManageAi` (Routes.jsx, config/navigation.js) — an extra gate
 * here could only ever hide the entire screen from somebody who cannot reach
 * it anyway. KnowledgeBases re-checks because ASKING is ungated there; there
 * is no ungated half of this one.
 */

/** A scope descriptor from the server, or nothing. */
const scopeEntry = (scopes, scope) => scopes.find((s) => s.scope === scope);

/** A scope key with no label from the server still has to render as something. */
const labelFor = (scopes, scope) => (
  scopeEntry(scopes, scope)?.label || scope || '—'
);

/** "—" rather than a blank cell, so a missing author reads as missing. */
const who = (name) => name || '—';

/** A mode as a word, falling back to its own value. */
const modeLabel = (value) => modeDescriptor(value)?.label || value || '—';

const readErr = (err, fallback) => (
  err.response?.data?.message || err.message || fallback
);

/**
 * A REFUSAL IS EITHER FIELD MESSAGES OR A BANNER, NEVER BOTH.
 *
 * This is the bug the owner hit. The field list was already being rendered —
 * but `setError(readErr(err))` ALSO ran, and `readErr` reads
 * `response.data.message`, which a ValidationError from this API sets to the
 * constant string "The validation has failed". `<Error>` renders above the
 * list, so the first thing on screen was
 *
 *   An error occured / The validation has failed
 *
 * with the sixteen sentences that actually named the problem pushed below it.
 * The owner reported seeing the generic error because the generic error was
 * there; adding the list without removing the banner fixed nothing they could
 * see. Hiding a useful message under a useless one is the same failure as not
 * having the useful one.
 *
 * So: when the server sent field messages, THEY are the error and the banner
 * is empty. When it did not (a 409, a network fault), the banner is the only
 * thing there is and it carries the real reason.
 */
const splitRefusal = (err, fallback) => {
  const errors = err?.response?.data?.errors;
  const fields = Array.isArray(errors) ? errors.filter((e) => e?.message) : [];
  return { fields, message: fields.length > 0 ? '' : readErr(err, fallback) };
};

function AgentBuilderArea() {
  const [scopes, setScopes] = useState([]);
  const [tools, setTools] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [scope, setScope] = useState('');
  const [payload, setPayload] = useState(null);
  const [loadingScopes, setLoadingScopes] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refusals, setRefusals] = useState([]);

  const [draftBody, setDraftBody] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  // The mode and the tool map are part of the draft, so they are editor state
  // beside the body and are saved by the same button. See the header.
  const [draftMode, setDraftMode] = useState('shadow');
  const [draftTools, setDraftTools] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedNotice, setSavedNotice] = useState(null);
  const [publishedNotice, setPublishedNotice] = useState(null);

  // `{ version }` — which publish the dialog is confirming. A publish in
  // flight is tracked separately so the dialog can say so. Only ever the
  // draft's version: the server refuses a version that is already published.
  const [pendingPublish, setPendingPublish] = useState(null);
  const [publishing, setPublishing] = useState(false);

  // An earlier version being READ: `{ version, data }`, or null. The history
  // rows carry no bodies, so this is one request on click and nothing at all
  // on page load. `readingVersion` is the one in flight.
  const [preview, setPreview] = useState(null);
  const [readingVersion, setReadingVersion] = useState(null);

  // The guidance box, so a tool name can be inserted where the caret is
  // rather than appended somewhere the author then has to find.
  const bodyRef = useRef(null);

  const published = payload?.published || null;
  const draft = payload?.draft || null;
  const history = payload?.history || [];
  const maxLength = maxLengthFor(payload);

  // The editor against what was last SAVED. Everything about publishing
  // hangs off this: unsaved text has no version, so it cannot be published.
  //
  // THE MODE AND THE TOOL MAP COUNT TOWARDS IT, because they are saved and
  // published with the body. A tool flipped to `auto` and not saved is as
  // unpublishable as a half-typed sentence, and for the same reason: publish
  // sends a stored version, so anything not stored is not in it.
  const savedBody = draft?.body || '';
  const savedConfig = draftConfigFrom(payload);
  const dirty = draftBody !== savedBody
    || draftNotes !== (draft?.notes || '')
    || draftMode !== savedConfig.mode
    || !sameAuthority(draftTools, savedConfig.toolAuthority);
  const overCap = draftBody.length > maxLength;

  /**
   * Load one scope. Also resets the editor to what the server now holds,
   * which is why it is only called on a scope change and after a write — it
   * would otherwise be able to throw away something half-typed.
   *
   * The CONFIGURATION the editor opens on is the draft's, or what is live if
   * there is no draft — `draftConfigFrom`, which exists so that an admin who
   * came to fix a typo cannot revoke a live version's tools by saving without
   * having looked at them.
   */
  const fetchScope = useCallback(async (which) => {
    setLoading(true);
    setError('');
    try {
      const data = await AiInstructionsService.getForScope(which);
      const config = draftConfigFrom(data);
      setPayload(data || null);
      setDraftBody(data?.draft?.body || '');
      setDraftNotes(data?.draft?.notes || '');
      setDraftMode(config.mode);
      setDraftTools(config.toolAuthority);
    } catch (err) {
      setPayload(null);
      setError(readErr(err, 'Failed to load the instructions for this scope'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // ONE read for the scope list, the tool catalogue and the authority
        // ranking — the server sends all three together precisely so the
        // dashboard holds no copy of the tool names.
        const catalogue = await AiInstructionsService.getCatalogue();
        if (!live) return;
        setScopes(catalogue.scopes);
        setTools(catalogue.tools);
        setAuthorities(catalogue.authorities);
        setScope((prev) => prev || catalogue.scopes?.[0]?.scope || '');
      } catch (err) {
        if (live) setError(readErr(err, 'Failed to load the instruction scopes'));
      } finally {
        if (live) setLoadingScopes(false);
      }
    })();
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!scope) return;
    setSavedNotice(null);
    setPublishedNotice(null);
    setPreview(null);
    fetchScope(scope);
  }, [scope, fetchScope]);

  const scopeLabel = labelFor(scopes, scope);

  /**
   * Save the draft: the prose, the notes, the mode and the tool map, in one
   * call, because they are one row.
   *
   * THE SAVE IS WHERE THE PROSE AND THE TOOLS ARE CHECKED AGAINST EACH OTHER.
   * The server refuses a body that names a tool the scope does not hold, and
   * refuses an authority above a tool's ceiling — both as field-level
   * messages that name the tool. `refusals` carries them to the screen; the
   * generic banner alone would say "The validation has failed".
   */
  const handleSaveDraft = async () => {
    setSaving(true);
    setError('');
    setRefusals([]);
    setSavedNotice(null);
    setPublishedNotice(null);
    try {
      await AiInstructionsService.saveDraft({
        scope,
        body: draftBody,
        notes: draftNotes.trim(),
        mode: draftMode,
        toolAuthority: draftTools,
      });
      // Refetched rather than read off the POST: the version the draft was
      // stored under is the server's to assign, and the publish button needs
      // the real one.
      await fetchScope(scope);
      setSavedNotice(true);
    } catch (err) {
      const { fields, message } = splitRefusal(err, 'Failed to save the draft');
      setRefusals(fields);
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmPublish = async () => {
    setPublishing(true);
    setError('');
    setRefusals([]);
    setSavedNotice(null);
    try {
      await AiInstructionsService.publish({ scope, version: pendingPublish.version });
      setPendingPublish(null);
      await fetchScope(scope);
      setPublishedNotice(pendingPublish.version);
    } catch (err) {
      setPendingPublish(null);
      // Publish re-runs the same checks on the STORED row, so the same
      // tool-naming refusal can arrive here too.
      const { fields, message } = splitRefusal(err, 'Failed to publish');
      setRefusals(fields);
      setError(message);
    } finally {
      setPublishing(false);
    }
  };

  /**
   * One tool's authority. `off` is kept in state and dropped on the way out.
   *
   * IT REFUSES AN AUTHORITY ABOVE THE TOOL'S CEILING, and that is not
   * belt-and-braces for its own sake. The radio is rendered `disabled`, which
   * is what a person meets — but `disabled` is a property of a control, not a
   * rule, and anything that reaches this function by another route (a
   * dispatched event, a future keyboard shortcut, a caller written later)
   * would otherwise put a value in the draft that the server will refuse and
   * that the screen would meanwhile display as configured.
   *
   * The same shape as the server's own arrangement: the gate refuses
   * (`aiToolAuthorityProblems`) and the belt under it clamps rather than
   * trusting what it is handed (`normaliseAiToolAuthority`). Ignoring only
   * ever leaves the draft as it was, so the worst case is a control that
   * appears not to respond — which is what a disabled control should do.
   */
  const handleToolChange = (key, authority) => {
    const tool = tools.find((t) => t.key === key);
    if (!tool || !isAuthorityAllowed(authorities, tool, authority)) return;
    setDraftTools((prev) => ({ ...prev, [key]: authority }));
  };

  /**
   * NAME A TOOL IN THE PROSE AND GRANT IT, IN ONE ACTION.
   *
   * The server refuses a save whose guidance names a tool the scope does not
   * hold. Those are two halves of one intention, so doing them together
   * satisfies the check BY CONSTRUCTION rather than leaving an author to meet
   * it as a refusal — which is exactly what went wrong: guidance written the
   * way the owner was asked to write it could not be saved at all.
   *
   * It is granted at the most the CODE allows: `auto` where the ceiling
   * permits, the ceiling otherwise. "Grant" has to mean "can actually call
   * it" — only `auto` is handed to the model — and for a booking write the
   * honest most is `propose`, which the reconciliation then reports as "will
   * not execute" rather than this pretending otherwise.
   *
   * Inserted AT THE CURSOR, because an author clicking this is mid-sentence
   * ("…then call "). Appending to the end instead would put the name
   * somewhere they then have to go and find.
   */
  const handleInsertTool = (key) => {
    const tool = tools.find((t) => t.key === key);
    if (!tool) return;

    const grant = isAuthorityAllowed(authorities, tool, EXECUTING_AUTHORITY)
      ? EXECUTING_AUTHORITY
      : tool.maxAuthority;
    handleToolChange(key, grant);

    const el = bodyRef.current;
    const at = el && Number.isInteger(el.selectionStart)
      ? el.selectionStart
      : draftBody.length;
    const before = draftBody.slice(0, at);
    const after = draftBody.slice(at);
    // A bare name is what the server's scanner reads, and what the model
    // sees. Spaced so it does not weld itself to the word it lands beside.
    const lead = before && !/\s$/.test(before) ? ' ' : '';
    const tail = after && !/^[\s.,;:)]/.test(after) ? ' ' : '';
    setDraftBody(`${before}${lead}${key}${tail}${after}`);

    if (el) {
      const caret = before.length + lead.length + key.length;
      // After React has written the new value, or the browser puts the caret
      // back at the end of the box and the next keystroke lands there.
      window.requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(caret, caret);
      });
    }
  };

  /**
   * Read one earlier version. A fetch per click, because the history list is
   * deliberately bodyless — shipping every version's text to render a table
   * of dates would cost a third of a megabyte on a scope with a long history.
   */
  const handleReadVersion = async (version) => {
    setReadingVersion(version);
    setError('');
    try {
      const data = await AiInstructionsService.getVersion({ scope, version });
      setPreview({ version, data });
    } catch (err) {
      setPreview(null);
      setError(readErr(err, `Failed to read version ${version}`));
    } finally {
      setReadingVersion(null);
    }
  };

  /**
   * Put an earlier version's words in the editor. THIS IS NOT A RESTORE and
   * cannot be: the server refuses to publish a version that is already
   * published, so the only way back to v3 is v3's text saved as a new draft
   * and published forward. It publishes nothing and saves nothing — the
   * admin still has to do both, deliberately, as for any other edit.
   *
   * The notes are NOT carried over. They are the changelog line for the
   * publish that happened back then; reusing them would label the new version
   * with an old reason, and the reason for going back is the thing worth
   * writing down.
   */
  const handleCopyIntoDraft = () => {
    setDraftBody(preview?.data?.body || '');
    setPreview(null);
  };

  // The draft sits in the history table as its own row, as the design draws
  // it. `history` from the server carries only PUBLISHED versions — a draft
  // has no publish to list — so it is prepended here rather than waited for.
  const historyRows = [
    ...(draft ? [{
      version: draft.version,
      published_by: draft.updated_by || draft.created_by,
      published_at: null,
      notes: draft.notes,
      isDraft: true,
    }] : []),
    ...history,
  ];

  return (
    <>
      <Error error={error} setError={setError} />

      {/* The server's own words for WHY it refused, each naming its field.
          The banner above says "The validation has failed"; this says "the
          guidance mentions the tool search_bookings, which this scope's agent
          does not hold". Only one of those is something to act on. */}
      <ValidationErrorList errors={refusals} />

      {publishedNotice && (
        <Alert variant="warning" dismissible onClose={() => setPublishedNotice(null)}>
          <strong>{`Version ${publishedNotice} is live now.`}</strong>
          {` The ${scopeLabel} agent is using it from its next job onwards.`}
        </Alert>
      )}

      {savedNotice && (
        <Alert variant="info" dismissible onClose={() => setSavedNotice(null)}>
          <strong>Draft saved. It is NOT live.</strong>
          {published
            ? ` The agent is still using published version ${published.version}.`
            : ' Nothing is published for this scope, so the agent is not'
              + ' using anything from this screen.'}
          {' Publish it when you want it to take effect.'}
        </Alert>
      )}

      <div className="ai-instructions">
        {/* ── the left rail: which agent, and what the text can do ── */}
        <aside className="ai-instructions-side">
          <div className="ai-panel">
            <Form.Group controlId="ai-instructions-scope">
              <Form.Label className="ai-panel-label">Agent</Form.Label>
              {loadingScopes ? (
                <div className="text-muted small">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading agents…
                </div>
              ) : (
                <Form.Select
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                  disabled={scopes.length === 0}
                >
                  {scopes.length === 0 && <option value="">No agents available</option>}
                  {scopes.map((s) => (
                    <option key={s.scope} value={s.scope}>{s.label || s.scope}</option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>
            {/* The scope's own description, as the server defines it — what
                this agent is for and what it does with a job. It arrives on
                the scopes payload and was being thrown away; it is the one
                sentence that says which agent you are about to change. */}
            {scopeEntry(scopes, scope)?.description && (
              <p className="ai-panel-note ai-scope-description">
                {scopeEntry(scopes, scope).description}
              </p>
            )}
            <p className="ai-panel-note mb-0">
              The list is fixed in code. New agents appear here when they are
              built to read guidance — you cannot add one from this screen.
            </p>
          </div>

          {/* The honest note, shortened to what somebody will actually read.
              It was four paragraphs; it is the same four claims. Not
              dismissible and not collapsed: an admin editing this must not
              walk away believing they were editing what the system is
              PERMITTED to do. */}
          <div className="ai-panel ai-panel--warn">
            <div className="ai-panel-title">
              <i className="fa fa-triangle-exclamation me-2" aria-hidden="true" />
              What guidance can and cannot do
            </div>
            <p className="mb-0">
              It steers how the agent
              {' '}
              <strong>classifies</strong>
              {' a situation — which wording it reads as a chargeable change,'
               + ' which note it reads as a flight number. It cannot give the'
               + ' agent a new action, and it cannot make it act where the code'
               + ' says hold: validation and permissions are enforced outside'
               + ' this text, and guidance that contradicts them loses.'}
            </p>
          </div>
        </aside>

        {/* ── the right column: live, draft, what publishing does, history ── */}
        <div className="ai-instructions-main">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2">Loading instructions…</p>
            </div>
          ) : (
            <>
              {/* LIVE. Read-only on purpose: the only way to change what the
                  agent is using is to publish a version, so there is no field
                  here that could be mistaken for one. */}
              <section className="ai-version ai-version--live">
                <header className="ai-version-head">
                  <div className="ai-version-head-main">
                    <span className="ai-version-pill ai-version-pill--live">LIVE NOW</span>
                    <span className="ai-version-number">
                      {published ? `Version ${published.version}` : 'Nothing published'}
                    </span>
                  </div>
                  {published && (
                    <span className="ai-version-byline">
                      {`published by ${who(published.published_by)} · ${formatDateTime(published.published_at) || '—'}`}
                    </span>
                  )}
                </header>
                <div className="ai-version-body">
                  {published ? (
                    <>
                      {/* WHAT IT IS ALLOWED TO DO, beside what it was told.
                          Both were frozen by the same publish, so a live
                          panel that showed only the prose would answer half
                          the question an admin opens this screen with. */}
                      <div className="ai-live-config">
                        <Badge
                          bg={modeOf(published) === 'live' ? 'warning' : 'secondary'}
                          text={modeOf(published) === 'live' ? 'dark' : undefined}
                        >
                          {modeLabel(modeOf(published))}
                        </Badge>
                        <span className="ai-live-config-tools">
                          {(() => {
                            const count = grantedTools(published.tool_authority).length;
                            return count === 1
                              ? '1 tool granted'
                              : `${count} tools granted`;
                          })()}
                        </span>
                      </div>
                      {published.notes && (
                        <p className="ai-version-notes">{published.notes}</p>
                      )}
                      <div className="ai-version-text" aria-label="Published instructions">
                        {published.body}
                      </div>
                    </>
                  ) : (
                    <p className="mb-0">
                      Nothing has been published for this scope yet, so no
                      guidance from this screen is reaching the agent. It is
                      running on what is in the code.
                    </p>
                  )}
                </div>
              </section>

              {/* WHAT THE MODEL ACTUALLY RECEIVES. Directly under the live
                  panel, because that panel's "it is running on what is in the
                  code" sentence is true and was unverifiable: the code-owned
                  prompt existed and appeared nowhere. This is the read that
                  answers "I still don't see the prompts". */}
              <ComposedPrompt
                scope={scope}
                scopeLabel={scopeLabel}
                publishedVersion={published?.version}
                draftVersion={dirty ? null : draft?.version}
              />

              {/* DRAFT. Every label on this panel says it is not live. */}
              <section className="ai-version ai-version--draft">
                <header className="ai-version-head">
                  <div className="ai-version-head-main">
                    <span className="ai-version-pill ai-version-pill--draft">DRAFT — NOT LIVE</span>
                    <span className="ai-version-number">
                      {draft ? `Version ${draft.version}` : 'No draft saved'}
                    </span>
                  </div>
                  {draft && (
                    <span className="ai-version-byline">
                      {/* `updated_by` is written by the API but is not a
                          declared schema path, so it never persists and always
                          reads back null. `created_by` is the one that is
                          stored — use it rather than rendering an em dash for
                          every draft anybody has ever saved. */}
                      {`saved by ${who(draft.updated_by || draft.created_by)} · ${formatDateTime(draft.updated_at) || '—'}`}
                    </span>
                  )}
                </header>
                <div className="ai-version-body">
                  <Form.Group controlId="ai-instructions-body">
                    <Form.Label className="visually-hidden">Draft guidance</Form.Label>
                    {/* No `maxLength`: the browser would silently stop the
                        typing at OUR mirrored number, which both hides the
                        server's real cap and loses the end of a paste. The
                        count warns, the server refuses, and the refusal is
                        shown. */}
                    <Form.Control
                      as="textarea"
                      ref={bodyRef}
                      rows={9}
                      value={draftBody}
                      onChange={(e) => setDraftBody(e.target.value)}
                      placeholder="The guidance this agent should follow."
                      className="ai-draft-textarea"
                    />
                  </Form.Group>

                  {/* An authoring aid for the box above, not another panel:
                      a rule like "quote only from the knowledge base" is
                      written blind otherwise. Collapsed, and fetches nothing
                      until it is opened. */}
                  <GuidanceKnowledgeBase />

                  <Form.Group className="mt-3" controlId="ai-instructions-notes">
                    <Form.Label className="ai-panel-label">Notes</Form.Label>
                    <Form.Control
                      type="text"
                      value={draftNotes}
                      onChange={(e) => setDraftNotes(e.target.value)}
                      placeholder="What changed and why, for whoever reads the history."
                    />
                  </Form.Group>

                  <div className="ai-draft-foot">
                    <span className={`ai-draft-count ${overCap ? 'is-over' : ''}`}>
                      {`${draftBody.length.toLocaleString('en-US')} / ${maxLength.toLocaleString('en-US')} characters`}
                      {overCap && ' — over the cap. The API will refuse this; shorten it. Nothing is cut for you.'}
                    </span>
                    <div className="ai-draft-actions">
                      <Button
                        variant="outline-secondary"
                        onClick={handleSaveDraft}
                        disabled={saving || !scope}
                      >
                        {saving ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving…
                          </>
                        ) : 'Save draft'}
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => setPendingPublish({ version: draft.version })}
                        disabled={!draft || dirty || saving}
                      >
                        {draft ? `Publish v${draft.version}…` : 'Publish…'}
                      </Button>
                    </div>
                  </div>

                  <p className="ai-panel-note mb-0">
                    {!draft && 'Nothing to publish yet — save a draft first.'}
                    {draft && dirty && 'Unsaved changes. Publish sends the SAVED version, not what is in the box — save first.'}
                    {draft && !dirty && published
                      && `Saving keeps this as a draft. The agent keeps using v${published.version} until you publish.`}
                    {draft && !dirty && !published
                      && 'Saving keeps this as a draft. Nothing is published for this scope, so the agent is using what is in the code.'}
                  </p>
                </div>
              </section>

              {/* WHAT IT MAY CALL. Below the prose because the prose is what
                  drives the tools, and because a refusal about a tool the
                  guidance names is resolved here. Saved by the button above:
                  one draft, one save. */}
              <AgentToolMap
                tools={tools}
                authorities={authorities}
                value={draftTools}
                mode={draftMode}
                publishedMode={published ? modeOf(published) : undefined}
                publishedValue={published?.tool_authority}
                body={draftBody}
                onChange={handleToolChange}
                onModeChange={setDraftMode}
                onInsertTool={handleInsertTool}
                disabled={saving}
              />

              {/* THE REHEARSAL, between the thing being edited and the thing
                  that makes it live. That is the order of the loop this
                  screen exists for: adjust, run it on an email whose answer
                  you know, read the trace, adjust again, publish. */}
              <AgentDryRun
                scope={scope}
                scopeLabel={scopeLabel}
                version={draft?.version}
                dirty={dirty}
                tools={tools}
              />

              {/* What the publish button is about to do, before it is pressed
                  — the dialog says it again at the moment of pressing. */}
              {draft && (
                <section className="ai-panel ai-panel--publish">
                  <div className="ai-panel-title">
                    {`Publishing v${draft.version} — what happens`}
                  </div>
                  <ul className="mb-0">
                    <li>
                      {`v${draft.version} becomes the guidance the ${scopeLabel} agent uses on its `}
                      <strong>next pass</strong>
                      . No deploy, no review.
                    </li>
                    <li>
                      {/* The mode and the tool map go live with the prose,
                          because they are the same version. Saying so here is
                          the difference between publishing a wording change
                          and unknowingly putting an agent into `live`. */}
                      {'Its mode and tool authorities go live with it: '}
                      <strong>{modeLabel(savedConfig.mode)}</strong>
                      {`, with ${grantedTools(savedConfig.toolAuthority).length} of`
                       + ` ${tools.length} tools granted.`}
                    </li>
                    <li>
                      {published
                        ? `v${published.version} is kept and stays readable. Nothing is overwritten.`
                        : 'Nothing is live for this scope today, so this is the first version to reach the agent.'}
                    </li>
                    <li>
                      {'Every decision from now records '}
                      <code>{`instructions_version: ${draft.version}`}</code>
                      , so its effect is measurable.
                    </li>
                    <li>
                      {`To go back, publish an earlier version's text again as v${draft.version + 1} — history is only ever added to.`}
                    </li>
                  </ul>
                </section>
              )}

              <section className="ai-panel ai-panel--flush">
                <div className="ai-panel-title ai-panel-title--bar">History</div>
                <Table className="ai-history" aria-label="Instruction version history">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Published by</th>
                      <th>When</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* The draft is a row, as the design draws it, AND a scope
                        with no publishes still says so — a draft is not a
                        publish, and one must not stand in for the other. */}
                    {history.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          Nothing has been published for this scope yet.
                        </td>
                      </tr>
                    )}
                    {historyRows.map((entry) => {
                      const isLive = !entry.isDraft && published?.version === entry.version;
                      return (
                        <tr key={`${entry.isDraft ? 'draft' : 'v'}-${entry.version}`}>
                          <td data-label="Version">
                            <span
                              className={`ai-history-badge ${isLive ? 'is-live' : ''} ${entry.isDraft ? 'is-draft' : ''}`}
                              title={isLive ? 'Live now' : undefined}
                            >
                              {`v${entry.version}`}
                            </span>
                            {/* The design marks the live row by the badge's
                                colour alone. Colour is not a label, so the
                                word is here too — visually hidden, because the
                                design's green is the sighted reader's cue. */}
                            {isLive && <span className="visually-hidden">Live</span>}
                          </td>
                          <td data-label="Published by">{who(entry.published_by)}</td>
                          <td data-label="When">
                            {entry.isDraft ? 'draft' : (formatDateTime(entry.published_at) || '—')}
                          </td>
                          <td data-label="Note">
                            {entry.notes || '—'}
                            {/* READ, not publish. Every published row is
                                frozen and the server refuses to publish one
                                again, so a publish button here could only
                                produce a refusal. Reading is the first step of
                                going back, and the only one this table can
                                offer — the design's own "v4 stays readable"
                                bullet is what this button is for. */}
                            {!entry.isDraft && (
                              <Button
                                size="sm"
                                variant="link"
                                className="ai-history-read"
                                onClick={() => handleReadVersion(entry.version)}
                                disabled={readingVersion !== null}
                              >
                                {readingVersion === entry.version ? 'Reading…' : 'Read this version'}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <p className="ai-panel-note ai-history-foot">
                  {'There is no revert: a published version is frozen, and publishing'
                   + ' one again is refused. Going back to an earlier version means'
                   + ' reading its text, saving it as a new draft and'
                   + ' publishing that — forward, as the next version, under your name.'
                   + ' The history carries the dates, not the text: read any version to'
                   + ' see exactly what it said, which is one request when you ask for it.'}
                </p>
              </section>

              {preview && (
                <section className="ai-panel">
                  <div className="ai-panel-title d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <span>{`Version ${preview.version}, word for word`}</span>
                    <Button size="sm" variant="outline-secondary" onClick={() => setPreview(null)}>
                      Close
                    </Button>
                  </div>
                  <p className="ai-panel-note">
                    {`Published by ${who(preview.data?.published_by)} on `}
                    {formatDateTime(preview.data?.published_at) || '—'}
                    . Read-only — this is the history, and the history is never
                    rewritten.
                  </p>
                  {preview.data?.notes && (
                    <p className="ai-version-notes">{preview.data.notes}</p>
                  )}
                  <div className="ai-version-text" aria-label={`Version ${preview.version} instructions`}>
                    {preview.data?.body}
                  </div>
                  {/* The honest action. Not "restore": copying puts the words
                      in the editor and leaves them unsaved and unpublished,
                      which is exactly what going back is. */}
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="mt-3"
                    onClick={handleCopyIntoDraft}
                  >
                    <i className="fa fa-pen me-2" aria-hidden="true" />
                    Copy this text into the draft
                  </Button>
                  <p className="ai-panel-note mb-0">
                    {`There is no button that makes version ${preview.version} live again,`
                     + ' because there is no such thing: it is published and frozen, and'
                     + ' publishing it a second time is refused. Copying puts its words in'
                     + ' the draft editor — then you save, and publish that as the next'
                     + ' version.'}
                    {dirty && ' You have unsaved changes in the editor; copying replaces them.'}
                  </p>
                </section>
              )}
            </>
          )}
        </div>
      </div>

      {pendingPublish && (
        <PublishInstructionsModal
          show
          scopeLabel={scopeLabel}
          version={pendingPublish.version}
          publishedVersion={published?.version}
          publishing={publishing}
          onConfirm={handleConfirmPublish}
          onCancel={() => setPendingPublish(null)}
        />
      )}
    </>
  );
}

export default AgentBuilderArea;
